// ---------------------------------------------------------------------------
// Static mock briefing for the landing page demo (PROJ-29)
// No database, no API calls — pure client-side content
//
// Fictional company: Muster Drive Systems GmbH (Automotive Tier-1)
// NOT based on any real company. Composite fictional scenario.
// ---------------------------------------------------------------------------

export const DEMO_COMPANY_NAME = 'Muster Drive Systems GmbH';

/**
 * Returns today's date formatted as DD.MM.YYYY for the demo briefing header.
 * This makes the demo always look current.
 */
export function getDemoDate(): string {
  return new Date().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Full demo briefing in Markdown format.
 * Matches the real briefing structure produced by the synthesis agent.
 * Scope mirrors a full production briefing: 11 sections, calculated coverage
 * amounts, 16-point risk table, NIS2 analysis, 7 conversation starters.
 */
export function getDemoBriefingMarkdown(): string {
  const today = getDemoDate();

  return `## 1. Executive Summary

Muster Drive Systems GmbH ist ein etablierter Automotive-Tier-1-Zulieferer aus Böblingen mit Spezialisierung auf elektronische Brems- und Antriebssteuergeräte. Das Unternehmen befindet sich in einer **kritischen Transformationsphase**: Umstieg vom klassischen Verbrenner-Geschäft auf E-Mobility-Komponenten, starke OEM-Konzentration (3 Kunden = 82% Umsatz) und eine laufende Qualitätseskalation bei einem Bremssensor-Bauteil (Q3 2024). Hinzu kommt ein Ransomware-Vorfall im Lieferkettennetzwerk (Dezember 2024) sowie eine ausstehende TISAX-Zertifizierung. Das Risikoprofil ist für den Mittelstand **überdurchschnittlich komplex**.

**Top-3 Handlungsempfehlungen:**

1. **Produkthaftpflicht & Rückrufkostenversicherung** — Laufende Qualitätseskalation bei Bremssensoren; Rückrufrisiko bei sicherheitskritischen Teilen; OEM-Vertragsstrafen (DS mind. 25 Mio. EUR)
2. **Cyber-Versicherung mit OT-Deckung** — Ransomware-Angriff auf Lieferanten Dezember 2024; TISAX-Lücke; OT-Vernetzung Produktionslinie; NIS2-Pflicht (DS mind. 5 Mio. EUR)
3. **Betriebsunterbrechung (24 Monate)** — Mono-Standort-Produktion; Lieferverpflichtungen gegenüber OEMs mit Vertragsstrafen; Sondermaschinen mit 8+ Monaten Wiederbeschaffungszeit

---

## 2. Unternehmensprofil

### Basisdaten

| Kriterium | Information |
|---|---|
| **Firma** | Muster Drive Systems GmbH |
| **Handelsregister** | HRB 754321, Amtsgericht Stuttgart |
| **Gründung** | März 1987 |
| **Stammkapital** | 2.500.000 EUR |
| **Hauptsitz** | Schönaicher Str. 103, 71034 Böblingen |
| **Rechtsform** | GmbH |
| **Mitarbeiter** | 387 (davon 45 in F&E, 210 in Produktion) |
| **Umsatz** | 58,4 Mio. EUR (2024); 52,1 Mio. EUR (2023) → +12,1% |
| **Bilanzsumme** | 41,2 Mio. EUR (2024) |
| **Eigenkapital** | 14,8 Mio. EUR (EK-Quote: 35,9%) |
| **Branche** | Kraftfahrzeugteile/-zubehör (NACE C29.32) |
| **Website** | www.muster-drive-systems.de |

### Gesellschafterstruktur

| Gesellschafter | Anteil | Hintergrund |
|---|---|---|
| Capitalmuster Beteiligungen GmbH & Co. KG | 67% | Private-Equity-Gesellschaft (seit 2021, MBO-Finanzierung) |
| Klaus Musterberg (Gründerfamilie) | 22% | Gründer, nicht mehr operativ tätig |
| Dr. Sandra Musterberg (Gründerfamilie) | 11% | Tochter des Gründers, nicht operativ tätig |

**Hinweis:** PE-Gesellschafter mit Exitfenster 2026–2027 (typisch 5–7 Jahre Haltedauer). Verkaufsprozess oder Trade-Sale wahrscheinlich. Erhöhter Bedarf an sauberer Versicherungsdokumentation für Due Diligence.

### Geschichte & Entwicklung

Muster Drive Systems wurde 1987 von Klaus Musterberg als Zulieferer für hydraulische Bremssysteme gegründet. Der Durchbruch kam in den 1990ern mit der Verlagerung auf ABS-Steuergeräte-Elektronik. 2018 begann die Transformation in Richtung E-Mobility (regenerative Bremssysteme, e-Drive-Steuergeräte). 2021 übernahm Capitalmuster Beteiligungen 67% im Rahmen eines Management-Buyouts. Seitdem läuft eine Wachstumsstrategie mit Fokus auf Elektromobilität — jedoch mit erheblichem Transformationsrisiko, da 58% des aktuellen Umsatzes noch aus Verbrenner-Komponenten stammen.

### Konzernstruktur & Standorte

| Gesellschaft | Standort | Funktion |
|---|---|---|
| Muster Drive Systems GmbH (HQ) | Böblingen, Baden-Württemberg | Hauptsitz, F&E, Vertrieb |
| Muster Drive Systems Fertigung GmbH | Böblingen (selber Campus) | Produktion, Logistik |
| Muster Drive Systems Kft. | Győr, Ungarn | Verlängerte Werkbank, Kleinserienproduktion |
| Muster Drive Systems LLC | Auburn Hills, Michigan, USA | Vertriebsbüro (US-OEM-Geschäft) |

### Produkte & Märkte

| Produktgruppe | Anteil Umsatz | Hauptkunden |
|---|---|---|
| Elektronische Steuergeräte Bremssysteme (ESC, ABS, EBD) | 42% | OEM-A (dt. Volumenhersteller), OEM-B (dt. Premium) |
| E-Drive-Steuergeräte & Inverter-Elektronik | 24% | OEM-B, OEM-C (europäischer Hersteller) |
| Sensorik (Raddrehzahl, Beschleunigung) | 19% | OEM-A, OEM-B, Tier-1-Systemintegratoren |
| Prüf- & Kalibriersysteme (Eigenentwicklung) | 15% | Intern + 3rd-Party |

**Kundenkonzentration:**

- OEM-A: 44% Umsatzanteil (kritisch!)
- OEM-B: 28% Umsatzanteil
- OEM-C: 10% Umsatzanteil
- Sonstige: 18%

→ **Top-3-Kunden = 82% Umsatz. Ausfall eines OEM-Auftrags = existenzbedrohend.**

---

## 3. Geschäftsführung & Key Persons

### Geschäftsführung

| Kriterium | Martin Musterfeld | Dipl.-Ing. Petra Musterová |
|---|---|---|
| **Position** | Geschäftsführer (CEO) | Geschäftsführerin (CTO) |
| **GF seit** | 2021 (MBO) | 2019 |
| **Alter** | 51 Jahre (geb. 1974) | 46 Jahre (geb. 1979) |
| **Hintergrund** | Ehem. VP Sales bei Continental-Tochter; MBA Mannheim | Dipl.-Ing. Fahrzeugtechnik KIT Karlsruhe; 12 Jahre Bosch Bremsenentwicklung |
| **Insolvenzhistorie** | Keine bekannt | Keine bekannt |
| **Weitere Mandate** | Beirat Capitalmuster | – |

### Prokuristen / Führungsebene 2

| Name | Funktion | Prokura |
|---|---|---|
| Thomas Musterkamp | CFO (seit Jan. 2023) | Einzelprokura |
| Sabine Musterle | Head of Quality & Compliance | Einzelprokura |
| Jörg Musterbach | Head of Operations / Werksleiter | Gesamtprokura |

### Key-Person-Risiko-Bewertung

| Faktor | Bewertung |
|---|---|
| **Gesamtbewertung** | **🟠 ERHÖHT** |
| CEO Musterfeld | Trägt OEM-Beziehungen, PE-Kommunikation, Exit-Prozess — bei Ausfall: Deal-Risiko |
| CTO Musterová | Einzige Person mit tiefem Bremssystem-Know-how + E-Drive-Roadmap; schwer zu ersetzen |
| Head of Quality | Sabine Musterle koordiniert laufende Qualitätseskalation; Ausfall in kritischer Phase |
| Nachfolge-Status | Für CEO und CTO keine erkennbare Nachfolgeregelung; PE-Board als Sicherheitsnetz |

---

## 4. Finanzkennzahlen

| Kennzahl | 2024 | 2023 | 2022 | Bewertung |
|---|---|---|---|---|
| **Umsatz** | 58,4 Mio. EUR | 52,1 Mio. EUR | 47,3 Mio. EUR | 🟢 +12,1% Wachstum |
| **EBIT** | 3,5 Mio. EUR | 3,9 Mio. EUR | 4,1 Mio. EUR | 🟡 Sinkend (Transformationskosten) |
| **EBIT-Marge** | 6,0% | 7,5% | 8,7% | 🟡 Margendruck durch E-Mobility-Invest |
| **Bilanzsumme** | 41,2 Mio. EUR | 37,8 Mio. EUR | 34,5 Mio. EUR | Wachsend |
| **Eigenkapital** | 14,8 Mio. EUR | 13,1 Mio. EUR | 11,9 Mio. EUR | 🟡 EK-Quote 35,9% (unter Branchenschnitt ~42%) |
| **Mitarbeiter** | 387 | 362 | 341 | Kontinuierlich wachsend |

*Quelle: North Data Premium*

### Forderungsanalyse (Proxy)

| Kennzahl | Schätzung | Begründung |
|---|---|---|
| Forderungsquote | 18–25% des Umsatzes | OEM-Zahlungsziele typisch 45–90 Tage; Jahresende-Bestände höher |
| Geschätzte Forderungen | 10,5–14,6 Mio. EUR | 58,4 Mio. EUR × 18–25% |
| DSO (Proxy) | 65–90 Tage | OEM-Standard in der Automobilzulieferbranche |
| Forderungsrisiko | 🟡 Mittel | Konzentration auf 3 OEMs; Ausfall eines OEM = >50% der Forderungen gefährdet |

→ **Warenkreditversicherung / Forderungsausfallversicherung prüfen**

### Finanzierungsstruktur (PE-kontext)

| Finanzierungsart | Volumen (Schätzung) | Details |
|---|---|---|
| PE-MBO-Fremdfinanzierung | ~18 Mio. EUR | Bankdarlehen (Capitalmuster-Akquisition 2021); geschätzte Restlaufzeit |
| Investitionskredit E-Mobility | ~5 Mio. EUR | Modernisierung Fertigungslinie (KfW-gefördert, Schätzung) |
| Betriebsmittellinien | ~4 Mio. EUR | Kontokorrent für Vorleistungen gegenüber OEM-Abrufen |

**Leverage-Risiko:** PE-finanzierte Unternehmen haben typisch höhere Verschuldung. Bei sinkender EBIT-Marge (6,0%) und steigenden Zinsen kann Covenants-Bruch drohen. Kreditversicherung / Kautionsversicherung besonders relevant.

---

## 5. Versicherungsanalyse & Empfehlungen

### Branchen-Pflicht-Matrix — Trigger-Check

| Trigger | Zutreffend? | Pflicht-Versicherungen |
|---|---|---|
| Produktion / Serienfertigung | ✅ JA (387 MA, 2 Fertigungsstandorte) | Maschinenbruch, BU, Produkthaftpflicht |
| Sicherheitskritische Bauteile | ✅ JA (Bremssysteme, ESC) | **Produkthaftpflicht (erhöhte DS!)**, Rückrufkostenversicherung |
| OEM-Lieferverpflichtungen | ✅ JA (Rahmenverträge mit Vertragsstrafen) | **BU (24 Monate)**, Kautionsversicherung |
| Export / international | ✅ JA (Ungarn, USA) | Transportversicherung, Auslandskranken, Exportkreditversicherung |
| B2B mit Zahlungszielen | ✅ JA (45–90 Tage OEM-Ziel) | Warenkreditversicherung, Forderungsausfall |
| Tech / vernetzte Produktion | ✅ JA (OT-Netz, IATF 16949 vernetzt) | **Cyber (OT-Deckung PFLICHT!)** |
| PE-finanziert / Leverage | ✅ JA (MBO 2021, Covenant-Risiko) | **D&O**, W&I-Versicherung, Kautionsversicherung |
| Auslandsmitarbeiter | ✅ JA (Ungarn, USA, Dienstreisen) | Auslandskranken, Gruppen-Unfall |
| Qualitätseskalation laufend | ✅ JA (Bremssensoren Q3 2024) | Rückrufkostenversicherung **DRINGEND** |
| Ransomware-Vorfall Lieferkette | ✅ JA (Dezember 2024) | Cyber-Versicherung **DRINGEND** |

**→ ALLE 10 Trigger erfüllt. Erhöhtes Risikoprofil für Automotive-Mittelstand.**

### Vollständige Versicherungsempfehlungen

#### Kategorie 1: Haftpflicht-Versicherungen

| Sparte | Deckungssumme | Berechnung | Begründung |
|---|---|---|---|
| **Betriebshaftpflicht** | **15 Mio. EUR** | MAX(Umsatz × 0,25; 10 Mio.) = MAX(14,6; 10) → 15 Mio. | OEM-Anforderungen typisch 10–15 Mio. DS; Personenschäden durch fehlerhafte Bremsteile |
| **Produkthaftpflicht (erweitert)** | **25 Mio. EUR** | Umsatz × 0,40 = 23,4 Mio. → 25 Mio.; sicherheitskritische Kategorie: erhöhter Faktor | **Bremssysteme = höchste Schadenkategorie im Automotive;** OEM-A fordert vertraglich mind. 20 Mio. DS; USA-Exposure (Michigan-Büro) |
| **Rückrufkostenversicherung** | **10 Mio. EUR** | Max. Charge Bremssensoren: 180.000 Einheiten × 45 EUR Einbaukosten = 8,1 Mio. + Prüfkosten | **AKUT:** Laufende Qualitätseskalation Q3 2024 — Rückruf nicht ausgeschlossen; OEM-A-Vertragsstrafe: bis 5% des Jahresumsatzes |
| **Umwelthaftpflicht** | **3 Mio. EUR** | Standard 3 Mio. EUR für Elektronikfertigung mit Lösungsmitteln/Reinigungsanlagen | Elektronikfertigung (Lötmittel, Flussmittel, Reinigungschemikalien); Kühlschmierstoffe; Böblingen: Trinkwasserschutzzone prüfen |
| **Berufshaftpflicht / E&O** | **5 Mio. EUR** | Umsatz × 8% = 4,7 Mio. → 5 Mio. | Software-Anteil der Steuergeräte wächst (OTA-Updates); Softwarefehler in sicherheitskritischen Systemen = erhöhtes E&O-Risiko |

#### Kategorie 2: Management-Versicherungen

| Sparte | Deckungssumme | Berechnung | Begründung |
|---|---|---|---|
| **D&O-Versicherung** | **10 Mio. EUR** | Bilanzsumme × 25% = 10,3 Mio.; PE-finanziert: erhöhter Faktor | PE-Gesellschafter mit Haftungsrisiken; Qualitätseskalation = GF-Haftung; NIS2-GF-Haftung; Covenant-Bruch = Bankenhaftung; Exit-Prozess = Prospekt-/Transaktionshaftung |
| **W&I-Versicherung** | **Vor Exit-Prozess abschließen** | Transaktionswert (KI-Schätzung: 5–8× EBITDA = 17–28 Mio.) × 10–20% | PE-Exit 2026–2027 erwartet; Gewährleistungsansprüche aus Kaufvertrag; laufende Qualitätseskalation als Haftungsrisiko für Verkäufer |
| **Strafrechtsschutz Manager** | **1 Mio. EUR** | Standard für produzierendes Gewerbe | OWiG-Bußgelder (Arbeitsschutz, NIS2, DSGVO); potenzielle strafrechtliche Relevanz bei Bremssystemmängeln |

#### Kategorie 3: Sach- und Technische Versicherungen

| Sparte | Deckungssumme | Berechnung | Begründung |
|---|---|---|---|
| **Gebäude-/Inhaltsversicherung** | **18 Mio. EUR** | Sachanlagen ~40% der Bilanzsumme × 1,1 Sicherheit = 18,1 Mio. (KI-Schätzung) | Fertigungsgebäude Böblingen + Ungarn-Standort; Reinraumanlagen; Hochregallager |
| **Maschinenbruch (MBBV)** | **12 Mio. EUR** | Maschinenpark: SMT-Linien, Prüfanlagen, Sondermaschinen — Neuwert Schätzung 10–12 Mio. | Spezialisierte Platinenfertigung; SMT-Bestückungsautomaten (Wiederbeschaffung 6–10 Monate!); Sonderprüfstände Eigenentwicklung |
| **Elektronikversicherung** | **5 Mio. EUR** | IT-Infrastruktur + Prüflabore: Neuwert geschätzt 4–6 Mio. | IATF-16949-Prüflabor; vernetzter Maschinenpark (OT); Engineering-Workstations (CAD/Simulation) |
| **Montageversicherung** | **2 Mio. EUR** | Max. Einzelauftrag Kundeninstallation: ~1,5 Mio. | Inbetriebnahme von Prüfsystemen beim Kunden; Schulungsfahrten |

#### Kategorie 4: Betriebsunterbrechung

| Sparte | Deckungssumme | Berechnung | Begründung |
|---|---|---|---|
| **BU / Feuer-BU** | **35 Mio. EUR (24 Monate Haftzeit)** | Jahresumsatz 58,4 Mio. × 35% Rohertragsmarge (KI-Schätzung) × 2 Jahre = 40,9 Mio.; konservativ 35 Mio. | **PFLICHT 24 MONATE:** OEM-Lieferverpflichtungen; Vertragsstrafe OEM-A: bis 5% Jahresumsatz p.a. bei Lieferausfall; Maschinenpark mit 6–10 Monaten Wiederbeschaffungszeit |
| **Maschinen-BU** | **10 Mio. EUR** | Rohertrag × 35% / 2 = 10,2 Mio. | Fertigungslinie = Single Point of Failure; SMT-Sondermaschinen |
| **Lieferanten-Ausfall (Rückwirkungsschaden)** | **8 Mio. EUR** | Umsatz × 14% = 8,2 Mio. | Halbleiter-Abhängigkeit (NXPI, Infineon); Single-Source bei Spezialchips; Supraleiter-Engpässe (E-Mobility) |

#### Kategorie 5: Transport & Logistik

| Sparte | Deckungssumme | Berechnung | Begründung |
|---|---|---|---|
| **Transportversicherung (Jahrespolice)** | **2 Mio. EUR je Einzelsendung** | Max. Wochensendung OEM-A: ~1,5 Mio. EUR Warenwert | JIT/JIS-Lieferungen an OEM-Werke; Transporte Böblingen ↔ Ungarn; Luftfracht bei Eillieferungen |
| **Werkverkehrsversicherung** | **500.000 EUR** | Eigene Transporter (ca. 8 Fahrzeuge) | Shuttle Böblingen ↔ Kundenlager |

#### Kategorie 6: Cyber & Digitale Risiken

| Sparte | Deckungssumme | Berechnung | Begründung |
|---|---|---|---|
| **Cyber-Versicherung (IT + OT)** | **5 Mio. EUR** | NIS2-betroffen + Mittelstand → Minimum 3–5 Mio.; Ransomware-Vorfall Lieferkette erhöht Dringlichkeit | **HÖCHSTE DRINGLICHKEIT:** Ransomware-Angriff auf Tier-2-Lieferanten Dez. 2024 mit Auswirkung auf eigene OT-Netzwerke; TISAX-Lücke; OEM-A fordert TISAX Level 2 bis Q3 2025; OTA-Software-Updates als neuer Angriffsvektor |
| **Cyber-BU** | **In Cyber-Police integriert, 60 Tage** | Tagesumsatz 58,4 Mio. / 365 = ~160k EUR × 60 Tage = 9,6 Mio.; gedeckelt durch Hauptpolice | Ransomware = Produktionsstillstand; Lieferverzug OEM = sofortige Vertragsstrafe |
| **Daten-/Privacy-Versicherung** | **2 Mio. EUR** | Standard bei internationalem Datentransfer (Ungarn, USA) + OEM-Datenaustausch | DSGVO-Risiko; OEM-Konstruktionsdaten (vertraulich); Mitarbeiterdaten 387 MA |

#### Kategorie 7: Kredit & Forderungen

| Sparte | Deckungssumme | Berechnung | Begründung |
|---|---|---|---|
| **Warenkreditversicherung** | **12 Mio. EUR** | Forderungsbestand 10,5–14,6 Mio. × 85% = 9–12,4 Mio. | Konzentration auf 3 OEMs; OEM-Insolvenz oder Produktionsstopp = Totalausfall; Transformation E-Mobility = OEM-Volumen-Volatilität |
| **Kautionsversicherung** | **5 Mio. EUR** | Bürgschaftslimit für OEM-Lieferverträge; Anzahlungsbürgschaften bei Sondermaschinen-Aufträgen | Rahmenverträge OEM-A erfordern Erfüllungsbürgschaften; PE-Leverage-Struktur erschwert Bankbürgschaften |

#### Kategorie 8: Personen & Key Person

| Sparte | Deckungssumme | Berechnung | Begründung |
|---|---|---|---|
| **Key-Person-Versicherung (Martin Musterfeld)** | **5 Mio. EUR** | EBITDA × 3 Jahre = ~5,2 Mio. | CEO = Schlüsselperson für PE-Exit 2026–2027; OEM-Beziehungsträger; Deal-Risiko bei Ausfall |
| **Key-Person-Versicherung (Petra Musterová)** | **4 Mio. EUR** | EBITDA × 2,5 Jahre = 4,4 Mio. | CTO = einzige Person mit vollständigem Bremssystem- + E-Drive-Know-how; unersetzbar in laufender Qualitätseskalation |
| **Gruppenunfallversicherung** | **387 MA — Gruppenvertrag** | 387 MA × 2 Jahresgehälter (Ø 48k) = 37 Mio. Gesamtvolumen; versichert als Gruppenvertrag mit gedeckeltem Budget | Produktionsumfeld, Maschinen, Hubstapler, Nachtschicht; Ungarn-Standort |
| **Auslandskrankenversicherung** | **Gruppenvertrag** | Dienstreisen: OEM-Werke Deutschland/Europa, USA (Michigan), Ungarn | USA-Standort: Krankenkosten ohne Versicherung existenzbedrohend |

#### Kategorie 9: Rechtsschutz

| Sparte | Deckungssumme | Begründung |
|---|---|---|
| **Firmenrechtsschutz** | **1 Mio. EUR** | Arbeitsrecht (387 MA, ständige Einstellungen/Trennungen); Vertragsrecht (OEM-Lieferstreitigkeiten); Steuerrecht |
| **Patent-/IP-Rechtsschutz** | **2 Mio. EUR** | Eigenentwicklungen Bremssensorik + Prüfsysteme; E-Drive-Patente in Anmeldung; Wettbewerbsdruck Continental/Bosch |

---

## 6. Benefits & Betriebliche Vorsorge

### Recherchierte Benefits

| Benefit | Status | Quelle |
|---|---|---|
| Betriebliche Altersvorsorge (bAV) | ✅ Basis-Modell vorhanden | Karriereseite + 4 Stellenanzeigen |
| Betriebliche Krankenversicherung (bKV) | ❌ Nicht erkennbar | |
| Gruppenunfallversicherung | ❌ Nicht erkennbar | |
| VWL | ✅ Vorhanden | Karriereseite + 3 Stellenanzeigen |
| Firmenfitness / Gesundheit | ✅ Vorhanden | Karriereseite + 2 Stellenanzeigen |
| ESOP / Mitarbeiterbeteiligung | ❌ Nicht erkennbar | |

### Cross-Selling-Potenzial Vorsorge (PFLICHT-Berechnung)

| Produkt | MA-Zahl | Monatsbeitrag (Schätzung) | Jahresvolumen |
|---|---|---|---|
| **bAV (Upgrade/Strukturierung)** | 387 MA | 120 EUR/MA/Monat (Facharbeiter-Niveau) | **55.728 EUR/Jahr** |
| **bKV** | 387 MA | 45 EUR/MA/Monat (Budget-Tarif) | **20.898 EUR/Jahr** |
| **Gruppen-BU (Führungskräfte + Ingenieure)** | 60 MA | 90 EUR/MA/Monat | **64.800 EUR/Jahr** |
| **Gesamt Cross-Selling Vorsorge** | | | **~141.426 EUR/Jahr** |

**→ bKV als Recruiting-Argument:** Fachkräftemangel Elektronikfertigung/Embedded-Software besonders ausgeprägt. Konkurrenz mit Bosch/Continental = Benefits als Differenzierungsmerkmal entscheidend.

---

## 7. Risiko-Recherche

| # | Kategorie | Ergebnis | Details |
|---|---|---|---|
| 1 | **Qualitätseskalation / Rückruf** | ⚠️ **FUND** | **Q3 2024:** Serienreklamation Raddrehzahlsensor Typ MDS-4800 bei OEM-A. Betroffen: ca. 12.000 Fahrzeuge (Produktionsjahr 2023/2024). Ursache: Fertigungstoleranzproblem beim Verguss. Aktuell in Klärung — kein öffentlicher Rückruf ausgelöst, aber OEM-A-Eskalationsstufe 3 aktiv. Rückrufrisiko: erhöht. |
| 2 | **Cyberangriff / IT-Sicherheitsvorfall** | ⚠️ **FUND** | **Dezember 2024:** Ransomware-Angriff auf Tier-2-Lieferanten (Leiterplattenhersteller). Laterale Ausbreitung über gemeinsames EDI-System auf Muster Drive Systems OT-Netzwerk. Produktionsstillstand 18 Stunden. Kein Datenverlust bestätigt, aber Forensik noch nicht abgeschlossen. TISAX-Assessment noch nicht durchgeführt — OEM-A-Forderung läuft seit März 2024. |
| 3 | **Arbeitsunfall / Verletzung** | ✅ Kein Fund | Keine öffentlichen Berichte; BGHM-Mitglied (BG Metall + Elektro) |
| 4 | **Klage / Rechtsstreit** | ✅ Kein Fund | Keine öffentlichen Rechtsstreitigkeiten; interne OEM-Eskalation läuft (nicht öffentlich) |
| 5 | **Insolvenz / Zahlungsprobleme** | ✅ Kein Fund | PE-finanziert, aber stabile EK-Basis; Covenants-Risiko bei sinkender EBIT-Marge beobachten |
| 6 | **Strafe / Bußgeld / Behörde** | ✅ Kein Fund | Keine bekannten Bußgelder; IATF 16949 zertifiziert |
| 7 | **Brand / Explosion / Unfall** | ✅ Kein Fund | Kein Produktionsunfall dokumentiert |
| 8 | **Umweltverschmutzung** | ✅ Kein Fund | Elektronikfertigung; keine bekannten Umweltvorfälle; Chemikalienmanagement geprüft |
| 9 | **Skandal / Betrug / Unterschlagung** | ✅ Kein Fund | Keine Vorwürfe bekannt; interne Revision PE-Standard |
| 10 | **Produkthaftung / Gewährleistung** | ⚠️ Latent | Keine bekannten Klagen; laufende Qualitätseskalation (Nr. 1) schafft latentes Produkthaftungsrisiko |
| 11 | **OEM-Konzentrationsrisiko** | ⚠️ **STRUKTURELL** | OEM-A = 44% Umsatz. Einstellung OEM-A-Plattform oder Insourcing durch OEM-A würde Existenz gefährden. E-Mobility-Transition schafft Volumen-Unsicherheit. |
| 12 | **Transformation Verbrenner → E-Mobility** | ⚠️ **STRATEGISCH** | 58% Umsatz noch aus Verbrenner-Komponenten. OEM-A hat ICE-Auslaufszenario 2028 kommuniziert. Zeitdruck für E-Mobility-Anteil: von 24% auf >60% in 4 Jahren. Transformationsrisiko = erhöht. |
| 13 | **Auszeichnungen / Positive Signale** | ⭐ Mehrere | IATF 16949 zertifiziert; Q-Award OEM-A (2022); mehrfach als "Top-100 Automobilzulieferer Baden-Württemberg" gelistet; Mitglied im Verband der Automobilindustrie (VDA) |
| 14 | **Kununu / Mitarbeiterbewertungen** | 🟡 Gemischt | 3,4 von 5 Sternen (Schätzung). Häufige Kritikpunkte: Führungskommunikation nach MBO, hohe Schichtbelastung Produktion. Fluktuation erhöht. |
| 15 | **Stellenanzeigen (offene Stellen)** | Zahlreiche | Aktives Hiring: Embedded-Software-Entwickler (3 Stellen), Qualitätsingenieure (2 Stellen), TISAX-Beauftragter (1 Stelle) — Lücke bestätigt |
| 16 | **News letzte 12 Monate** | 🟡 Wenig | Regional-Presse: Anlagenmodernisierung angekündigt (Okt. 2024); keine überregionale Berichterstattung; kein Pressesprecher aktiv |

### Risk-Map

🔴 **Kritische Risiken:**

- Qualitätseskalation Bremssensoren (OEM-A, Eskalationsstufe 3) — Rückruf nicht ausgeschlossen
- Ransomware-Vorfall Dez. 2024 — TISAX-Forderung OEM-A offen, Forensik läuft
- OEM-Konzentrationsrisiko (OEM-A = 44% Umsatz, kein Ausweichen kurzfristig möglich)

🟠 **Mittlere Risiken:**

- Verbrenner/E-Mobility-Transformationsrisiko (58% Umsatz Verbrenner, ICE-Auslauf OEM-A 2028)
- PE-Exit-Prozess 2026/2027 — W&I-Versicherungsschutz offen; Qualitätseskalation als Haftungsrisiko für Verkäufer
- EBIT-Margendruck (6,0% vs. Branchenschnitt 8–10%) — Covenant-Risiko bei Leverage
- Key-Person CTO Musterová (unersetzbar in Qualitätseskalation)
- TISAX Level 2 Deadline OEM-A: Q3 2025 — Eskalation bei Nichterfüllung

🟢 **Niedrige Risiken / Positive Signale:**

- IATF 16949 zertifiziert — Qualitätsmanagementsystem vorhanden
- Q-Award OEM-A (2022) — Vertrauensbasis trotz aktueller Eskalation
- EK-Quote stabil (35,9%), kein akutes Insolvenzrisiko
- PE-Gesellschafter als strategisches Sicherheitsnetz

### NIS2-Prüfung

1. **NACE-Code:** C29.32 — Herstellung von sonstigen Teilen und Zubehör für Kraftwagen → **Annex II NIS2: Verarbeitendes Gewerbe (C24–C30) = POTENZIELL ERFASST**
2. **Branche in Annex II?** → **JA** (NACE C29 ist gelistet)
3. **Größenschwelle:** >50 MA (387) UND >10 Mio. Umsatz (58,4 Mio.) → **JA, mittleres Unternehmen**
4. **Ergebnis:** ✅ **POTENZIELL BETROFFEN als „wichtige Einrichtung" (Annex II) — zu verifizieren mit Rechtsberater**
5. **Indirekte Betroffenheit:** OEM-A fordert TISAX Level 2 = de-facto NIS2-äquivalente Anforderungen an IT-Sicherheit
6. **Pflichten (wenn bestätigt):**
   - BSI-Registrierung erforderlich
   - 24h-Meldepflicht bei Cybervorfällen (**Dezember-2024-Vorfall hätte meldepflichtig sein können!**)
   - Dokumentiertes ISMS, MFA, Backup, Lieferkettensicherheit
   - GF-Haftung: Martin Musterfeld und Petra Musterová haften persönlich bei NIS2-Pflichtverletzung
   - Bußgelder: bis 10 Mio. EUR oder 2% Weltumsatz
7. **Empfehlung:** NIS2-Betroffenheit mit Rechtsberater klären; Cyber-Versicherung 5 Mio. EUR + D&O mit NIS2-Klausel; TISAX-Audit vorbereiten

---

## 8. Gesprächseinstiege

**1. Qualitätseskalation & Rückrufrisiko (DRINGENDSTER Einstieg)**

> „Herr Musterfeld, die Qualitätseskalation bei den MDS-4800-Sensoren — Stufe 3 bei OEM-A ist kein Routinevorgang. Wenn daraus ein Rückruf von 12.000 Fahrzeugen wird, reden wir über Rückholkosten von 5–8 Mio. EUR allein für die Einbauarbeiten, noch vor Haftungsansprüchen. Haben Sie aktuell eine Rückrufkostenversicherung, und deckt die den laufenden Vorfall ab?"

**2. Ransomware & TISAX-Forderung OEM-A**

> „Der Dezember-Vorfall über das EDI-System Ihres Lieferanten hat gezeigt: Die OT-Vernetzung ist die größte Angriffsfläche. OEM-A fordert TISAX Level 2 bis Q3 2025 — und Sie suchen gerade aktiv einen TISAX-Beauftragten. Was passiert vertraglich, wenn die Deadline nicht gehalten wird? Und war der 18-stündige Produktionsstillstand ein versicherter Schaden?"

**3. OEM-Konzentration & Betriebsunterbrechung**

> „44% Ihres Umsatzes kommt von einem einzigen Kunden. Wenn OEM-A einen Produktionsstopp verhängt oder ein Abrufvolumen um 30% reduziert, trifft das Sie sofort. Wie lange könnten Sie einen solchen Einbruch überbrücken — und deckt Ihre Betriebsunterbrechungsversicherung nicht nur Brand, sondern auch Lieferkettenunterbrechung ab?"

**4. PE-Exit & W&I-Versicherung**

> „Capitalmuster ist seit 2021 an Bord — das klassische Exitfenster öffnet sich 2026/2027. Jeder potenzielle Käufer wird im Due-Diligence-Prozess die Qualitätseskalation und den Cyber-Vorfall sehen. Eine W&I-Versicherung sichert Sie als Verkäufer vor Gewährleistungsansprüchen post-Closing ab. Hat Ihr M&A-Berater das bereits angesprochen?"

**5. Key-Person CTO in Qualitätseskalation**

> „Frau Musterová koordiniert gerade gleichzeitig die Qualitätseskalation mit OEM-A und die E-Drive-Roadmap. Sie ist die einzige Person, die beides kann. Was passiert operativ — und was kostet Sie das vertraglich — wenn sie drei Monate ausfällt? Eine Key-Person-Police kann den wirtschaftlichen Schaden begrenzen."

**6. EBIT-Margin & Covenant-Risiko**

> „Ihre EBIT-Marge ist von 8,7% auf 6,0% gesunken — bei gleichzeitigem PE-Leverage aus dem MBO 2021. Wenn der Trend anhält, könnten Bankenkreditvereinbarungen unter Druck geraten. Eine solide D&O-Versicherung schützt Sie persönlich, falls Gläubiger oder der PE-Gesellschafter Haftungsansprüche stellen. Ist Ihre D&O aktuell PE-tauglich?"

**7. Mitarbeiterbindung & bKV**

> „Sie haben drei offene Stellen für Embedded-Software-Entwickler — ausgerechnet die Leute, die Bosch und Continental ebenfalls suchen. Eine betriebliche Krankenversicherung kostet Sie 45 EUR pro Mitarbeiter im Monat und ist bis 50 EUR steuerfrei als Sachbezug. Wäre das nicht das einfachste Differenzierungsmerkmal gegen die Konzerne?"

---

## 9. Cross-Selling & Upselling

### Priorisierte Cross-Selling-Potenziale

| Priorität | Produkt | Begründung | Geschätztes Prämienvolumen |
|---|---|---|---|
| 🔴 1 | **Rückrufkostenversicherung** | Qualitätseskalation OEM-A akut; kein Versicherungsschutz erkennbar | 15.000–30.000 EUR/Jahr |
| 🔴 2 | **Cyber-Versicherung (OT-Deckung)** | Ransomware-Vorfall Dez. 2024; TISAX offen; NIS2 potenziell | 20.000–40.000 EUR/Jahr |
| 🔴 3 | **Produkthaftpflicht (Upgrade DS auf 25 Mio.)** | Sicherheitskritische Bremsbauteile; OEM-A-Anforderung; US-Exposure | 25.000–50.000 EUR/Jahr |
| 🟠 4 | **BU (24 Monate, inkl. Lieferkettenrückwirkung)** | OEM-Vertragsstrafen; Mono-Standort; Maschinenpark Sonderanfertigung | 18.000–35.000 EUR/Jahr |
| 🟠 5 | **D&O (PE-tauglich, NIS2-Klausel)** | GF-Haftung Qualitätseskalation + NIS2; PE-Exit-Vorbereitung | 8.000–18.000 EUR/Jahr |
| 🟠 6 | **Key-Person (Musterfeld + Musterová)** | CEO = Exit-kritisch; CTO = unersetzbar in Qualitätseskalation | 12.000–25.000 EUR/Jahr |
| 🟠 7 | **bAV-Programm (Strukturierung)** | Basis vorhanden, kein strukturiertes Programm; 387 MA | 55.728 EUR/Jahr Beitragsvolumen |
| 🟢 8 | **bKV-Programm** | Recruiting-Differenzierung; Fachkräftemangel Embedded-Software | 20.898 EUR/Jahr Beitragsvolumen |
| 🟢 9 | **W&I-Versicherung (Pre-Exit)** | PE-Exit 2026–2027; Qualitätseskalation als Haftungsrisiko | Einmalprämie 0,5–1% Transaktionswert |
| 🟢 10 | **Warenkreditversicherung** | OEM-Konzentrationsrisiko; Forderungen 10–15 Mio. EUR | 8.000–15.000 EUR/Jahr |

**Geschätztes Gesamtprämienvolumen (Neupotenzial Sachversicherungen):** 106.000–213.000 EUR/Jahr

### Private GF-Absicherung (Cross-Selling Geschäftsführer)

Martin Musterfeld (~49 Jahre): Manager-typische Absicherungslücken:
- Private Vermögensschaden-HP (D&O-Ergänzung)
- Berufsunfähigkeitsversicherung (oft nicht ausreichend bei PE-Managern)
- Altersvorsorge-Optimierung (Unternehmensanteile ≠ Liquidität bis Exit)

---

## 10. Datenlücken & Offene Fragen

| Datenlücke | Relevanz | Empfohlene Aktion |
|---|---|---|
| **Exakter Status Qualitätseskalation** | Kritisch — Rückrufkostenversicherung; Haftungsrisiko Verkäufer | Im Erstgespräch vertraulich erfragen |
| **Bestehende Versicherungsverträge** | Kritisch — Doppelversicherung vermeiden; Deckungslücken identifizieren | Vertragsübersicht im Erstgespräch anfordern |
| **TISAX-Status & OEM-A-Deadline** | Hoch — Cyber-Police-Konditionen beeinflusst; NIS2-Pflicht | Im Gespräch erfragen; Deadline Q3 2025 ansprechen |
| **Ransomware-Vorfall Details** | Hoch — versicherter Schaden? Forensik-Status? Datenverlust? | Vertraulich im Gespräch klären |
| **Maschinenpark-Neuwerte** | Hoch — Maschinenbruch, BU-Berechnung | Anlagenspiegel anfordern |
| **OEM-Vertragsdetails (Vertragsstrafen)** | Hoch — BU-Haftzeit und -summe | Vertragsauszug anfordern (unter NDA) |
| **PE-Exit-Zeitplan** | Hoch — W&I-Versicherung, D&O-Transition | Mit CEO im Vertrauen erfragen |
| **GF-Geburtsdaten** | Mittel — Key-Person-Prämienberechnung | Im Antragsprozess erfragen |
| **Ungarn-Standort: lokale Versicherungen** | Mittel — Doppelversicherung vs. Deckungslücke | Prüfen ob eigene Lokalpolice vorhanden |
| **USA-Exposure Produkthaftpflicht** | Hoch — US-Recht; punitive damages | Michigan-Büro: Verkauf von Produkten in USA? |
| **NIS2-Betroffenheit rechtlich bestätigt** | Mittel — Bußgeldrisiko; GF-Haftung | Mit Rechtsberater klären |

### Offene Fragen für das Erstgespräch

1. „Wie ist der aktuelle Stand der Qualitätseskalation mit OEM-A — und haben Sie bereits eine Rückrufkostenversicherung?"
2. „Welche Versicherungen haben Sie aktuell? Können wir eine Übersicht erhalten?"
3. „Der Ransomware-Vorfall im Dezember: Wurde das ein Versicherungsfall? Wie ist der aktuelle Forensik-Stand?"
4. „TISAX Level 2 bis Q3 2025 — wo stehen Sie gerade, und was passiert vertraglich bei Nichterfüllung?"
5. „Sind Sie NIS2-pflichtig? Hat das jemand rechtlich geprüft?"
6. „Wie ist der geplante Zeitrahmen für den PE-Exit, und haben Sie W&I-Versicherungen auf dem Radar?"
7. „Welche Vertragsstrafen sehen Ihre OEM-Rahmenverträge bei Lieferverzug vor?"
8. „Haben Sie eine bAV und bKV — und wenn ja, in welcher Form?"

---

## 11. Rechtlicher Hinweis

*Dieses Briefing dient ausschließlich der Gesprächsvorbereitung und stellt keine Versicherungsberatung im Sinne des §34d GewO dar. Alle Angaben basieren auf öffentlich verfügbaren Daten und können unvollständig oder veraltet sein. KI-generierte Schätzungen und Proxy-Werte sind entsprechend gekennzeichnet. Eine individuelle Beratung auf Basis der tatsächlichen Unternehmenssituation ist zwingend erforderlich.*

*Erstellt von salesbriefing.de*`;
}
